import React from 'react'
import PropTypes from 'prop-types'
import { Typography, Skeleton, Box } from '@stvgroup/stv-ui-components'

/**
 * Renders a clickable search result card with a skeleton loader state.
 *
 * Displays a poster or landscape image with an optional title overlay.
 * Navigates to a URL on click or keypress.
 *
 * @param {string} title - The title to display on the card overlay.
 * @param {string} image - The URL for the background image.
 * @param {boolean} usePoster - Determines the aspect ratio (poster or landscape).
 * @param {boolean} loading - If true, a skeleton loader is shown.
 * @param {boolean} showOverlay - If true, the title overlay is shown.
 * @param {string} url - The URL to navigate to on interaction.
 * @param {string} background - A custom background CSS value to override the image.
 */
const SearchCard = ({
  title, image, usePoster, loading, showOverlay, url, background,
}) => {
  const posterImage = image

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        // Aspect ratio padding-top trick. 133.33% for 3:4 (poster), 56.25% for 16:9 (landscape).
        paddingTop: usePoster ? '133.33%' : '56.25%',
        borderRadius: '8px',
        overflow: 'hidden',
        '&:focus-within .search-card-image': {
          boxShadow: theme => `inset 0 0 0 2px ${theme.palette.primary.main}`,
        },
      }}
    >
      {/* Skeleton is always rendered underneath to maintain layout,
      but only visible when loading */}
      <Skeleton
        variant="rect"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
      />
      {/* Render the actual card content when not loading */}
      {!loading && (
        <Box
          role="button"
          className="search-card-image"
          tabIndex={0}
          onMouseDown={() => {
            if (url) window.location.href = url
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && url) window.location.href = url
          }}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: background || `url(${posterImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            cursor: 'pointer',
            transition: 'opacity 0.3s ease-in-out',
            opacity: loading ? 0 : 1,
            outline: 'none',
            overflow: 'hidden',
            borderRadius: '8px',
          }}
        >
          {showOverlay && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  color: 'white',
                  margin: 0,
                  padding: '10px',
                  fontWeight: 700,
                  fontSize: {
                    xs: '18px', sm: '22px', md: '24px', lg: '26px',
                  },
                  lineHeight: '100%',
                  textAlign: 'center',
                  width: '100%',
                  textTransform: 'uppercase',
                  textShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
                }}
              >
                {title}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

SearchCard.propTypes = {
  title: PropTypes.string,
  image: PropTypes.string,
  usePoster: PropTypes.bool,
  loading: PropTypes.bool,
  showOverlay: PropTypes.bool,
  url: PropTypes.string,
  background: PropTypes.string,
}

SearchCard.defaultProps = {
  usePoster: false,
  loading: false,
  title: '',
  image: '',
  showOverlay: true,
  url: '',
  background: '',
}

export default SearchCard
